import { omit } from "ramda";
import { SEARCH_PROP } from "../constants";
import {
	type ActiveFilters,
	type BaseFundExplorerConfig,
	type Entity,
	type EntityKey,
	EntityKeySchema,
	type FilterConfig,
	type FilterOrFilterGroupConfig,
	type FiltersConfig,
	type Fund,
	type IndexedData,
	type IndexedDatum,
	type IndexedFund,
	type IndexedProfile,
	type IndexedShareclass,
	type ScalarValue,
} from "../types";

export type ApplyFilterArgs = {
	data: IndexedData;
	config: BaseFundExplorerConfig;
	filters: ActiveFilters;
};
export type EntityKeyValueMap = Record<EntityKey, ScalarValue[]>;
export type EntityKeyOperableValueMap = Record<EntityKey, OperableValue[]>;

export type OperableValue = {
	value: ScalarValue[];
	operand: FilterConfig["operand"];
};
export type FilterKeyValueMap = Record<string, OperableValue[]>;
export type SearchKeyValueMap = Record<string, ScalarValue[]>;
export type KeyValueMap = Record<string, unknown>;

/**
 *
 * @param param0 - data, config, and filters
 * @returns - filtered data
 *
 */
export default function applyFilters({
	data,
	config,
	filters,
}: ApplyFilterArgs) {
	const log = <T>(val: T) => {
		console.log(val);
		return val;
	};
	const toParentIndexedFunds = (entity: IndexedDatum) => {
		const fundIds = (entity.data.funds ?? [entity.data.fundId]) as string[];
		return fundIds.map((id) => data[id] as IndexedFund);
	};

	const toUniqueFundsReducer = (acc: IndexedFund[], f: IndexedFund) => {
		if (acc.some((a) => a.data.localId === f.data.localId)) {
			return acc;
		}
		acc.push(f);
		return acc;
	};
	const toFundData = (fund: IndexedFund) => fund.data;
	const isEntityType =
		<T extends IndexedDatum>(type: IndexedDatum["type"]) =>
		(obj: IndexedDatum): obj is T =>
			obj.type === type;
	const isEntityTypeProp =
		(type: IndexedDatum["type"]) =>
		([key]: [string, unknown]) =>
			key.startsWith(type);
	const toUnqualifiedPropEntries = ([key, val]: [string, unknown]) => [
		key.split(".")[1],
		val,
	];
	const filterEntityType = <T extends IndexedDatum>(
		type: IndexedDatum["type"],
	) => Object.values(data).filter(isEntityType<T>(type));

	const getUnqualifiedTypeFilters =
		(type: IndexedDatum["type"]) => (_filter: Record<EntityKey, unknown>) =>
			Object.fromEntries(
				Object.entries(_filter)
					.filter(isEntityTypeProp(type))
					.map(toUnqualifiedPropEntries),
			) as KeyValueMap;

	const getFilterConfig = (
		filterKey: string,
		filters: FilterOrFilterGroupConfig[],
	): FilterConfig | null => {
		let config: FilterConfig | null = null;
		for (const filter of filters) {
			if (filter.type === "group") {
				config = getFilterConfig(filterKey, filter.filters);
				if (config) {
					return config;
				}
			} else if (filter.key === filterKey) {
				return filter;
			}
		}
		return config;
	};

	const toEntityOperableValueMap = (_filters: ActiveFilters) =>
		Object.entries(_filters).reduce((acc, [filterKey, optionsKeys]) => {
			const maybeEntityKey = EntityKeySchema.safeParse(filterKey);
			if (maybeEntityKey.success) {
				acc[maybeEntityKey.data] = [
					{
						value: optionsKeys,
						operand: "=",
					},
				];
				return acc;
			}
			const _config = getFilterConfig(filterKey, config.filter.filters);
			if (!_config) {
				return acc;
			}
			const filterEntityKey = _config.entityKey;
			return _config.options
				.filter((o) => optionsKeys.includes(o.key))
				.reduce((_acc, option) => {
					const entityKey = option.entityKey ?? filterEntityKey;
					const operand = option.operand ?? _config.operand ?? "=";
					const oValue = {
						value: Array.isArray(option.value) ? option.value : [option.value],
						operand,
					};
					if (_acc[entityKey]) {
						_acc[entityKey].push(oValue);
					} else {
						_acc[entityKey] = [oValue];
					}
					return _acc;
				}, acc);
		}, {} as EntityKeyOperableValueMap);

	const searchFilters: EntityKeyValueMap =
		filters[SEARCH_PROP]?.length > 0
			? Object.fromEntries(
					config.search.searchOn.map((entityKey) => [
						entityKey,
						filters[SEARCH_PROP],
					]),
				)
			: {};

	const otherFilters: EntityKeyOperableValueMap = toEntityOperableValueMap(
		omit([SEARCH_PROP], filters),
	);

	try {
		const fundFilters = getUnqualifiedTypeFilters("fund");
		const shareclassFilters = getUnqualifiedTypeFilters("shareclass");
		const profileFilters = getUnqualifiedTypeFilters("profile");
		const profileEntities = filterEntityType<IndexedProfile>("profile");
		const scEntities = filterEntityType<IndexedShareclass>("shareclass");
		const fundEntities = filterEntityType<IndexedFund>("fund");

		const fundSearchFilters = fundFilters(searchFilters) as SearchKeyValueMap;
		const fundOtherFilters = fundFilters(otherFilters) as FilterKeyValueMap;
		const scSearchFilters = shareclassFilters(
			searchFilters,
		) as SearchKeyValueMap;
		const scOtherFilters = shareclassFilters(otherFilters) as FilterKeyValueMap;
		const profileSearchFilters = profileFilters(
			searchFilters,
		) as SearchKeyValueMap;
		const profileOtherFilters = profileFilters(
			otherFilters,
		) as FilterKeyValueMap;
		const isOnSearchSC = isOnSearch(scSearchFilters);
		const isOnSearchFund = isOnSearch(fundSearchFilters);
		const isOnSearchProfile = isOnSearch(profileSearchFilters);
		const isOnFilterSC = isOnFilter(scOtherFilters);
		const isOnFilterFund = isOnFilter(fundOtherFilters);
		const isOnFilterProfile = isOnFilter(profileOtherFilters);
		if (
			!isOnSearchFund &&
			!isOnSearchSC &&
			!isOnSearchProfile &&
			!isOnFilterFund &&
			!isOnFilterSC &&
			!isOnFilterProfile
		) {
			return fundEntities.map(toFundData);
		}

		const scSearchResults = isOnSearchSC
			? getEntityMatches(scEntities, scSearchFilters, {}).flatMap(
					toParentIndexedFunds,
				)
			: [];
		const fundSearchResults = isOnSearchFund
			? getEntityMatches(fundEntities, fundSearchFilters, {})
			: [];
		const profileSearchResults = isOnSearchProfile
			? getEntityMatches(profileEntities, profileSearchFilters, {}).flatMap(
					toParentIndexedFunds,
				)
			: [];
		const searchResults = (
			[
				...scSearchResults,
				...fundSearchResults,
				...profileSearchResults,
			] as IndexedFund[]
		).reduce(toUniqueFundsReducer, []);

		const useSearchResults =
			isOnSearchFund || isOnSearchSC || isOnSearchProfile;

		let filteredFunds = isOnFilterProfile
			? getEntityMatches(profileEntities, {}, profileOtherFilters)
					.flatMap(toParentIndexedFunds)
					.filter(
						(fund) =>
							!useSearchResults ||
							searchResults.some((sr) => sr.data.localId === fund.data.localId),
					)
			: useSearchResults
				? searchResults
				: fundEntities;

		filteredFunds = (
			isOnFilterFund
				? getEntityMatches(filteredFunds, {}, fundOtherFilters)
				: filteredFunds
		) as IndexedFund[];

		const scAvailableToFilter = isOnFilterSC
			? scEntities.filter((sce) =>
					filteredFunds.some((ff) => ff.data.localId === sce.data.fundId),
				)
			: [];

		filteredFunds = isOnFilterSC
			? getEntityMatches(scAvailableToFilter, {}, scOtherFilters).flatMap(
					toParentIndexedFunds,
				)
			: filteredFunds;
		return filteredFunds.reduce(toUniqueFundsReducer, []).map(toFundData);
	} catch (e) {
		console.error(e);
		return [];
	}
}

function getEntityMatches(
	items: IndexedDatum[],
	search: SearchKeyValueMap,
	other: FilterKeyValueMap,
) {
	const shouldSearch = isOnSearch(search);
	const shouldFilter = isOnFilter(other);
	if (!shouldFilter && !shouldSearch) {
		return [];
	}
	const searchExactMatches = shouldSearch
		? items.filter((item) => filterFn("search-exact", search, item.data))
		: items;
	const searchMatches =
		searchExactMatches.length > 0
			? searchExactMatches
			: items.filter((item) => filterFn("search", search, item.data));
	return shouldFilter
		? searchMatches.filter((item) => filterFn("filter", other, item.data))
		: searchMatches;
}

function filterFn<T extends Entity>(
	type: "search" | "search-exact" | "filter",
	filter: Record<string, ScalarValue[] | OperableValue[]>,
	item: T,
) {
	const keys = Object.keys(filter) as (keyof T)[];
	const toLowerCase = (val: unknown) => `${val}`.toLowerCase();
	const exactMatch = (val: string, kw: string) =>
		val.toLowerCase() === kw.toLowerCase();
	const simpleMatch = (val: string, kw: string) =>
		val.toLowerCase().includes(kw.toLowerCase());

	switch (type) {
		case "filter":
			return keys.every((key) => {
				return (filter[key as string] as OperableValue[]).some((oValue) => {
					if (oValue.value.length === 0) {
						return true;
					}
					const itemValue = item[key];
					const oValues = oValue.value.map(toLowerCase);
					if (Array.isArray(itemValue)) {
						return itemValue
							.map(toLowerCase)
							.some((val) => oValues.includes(val));
					}
					return oValues.includes(`${itemValue}`.toLowerCase());
				});
			});
		case "search":
			return keys.some((key) =>
				filter[key as string].some((v) => simpleMatch(`${item[key]}`, `${v}`)),
			);
		case "search-exact":
			return keys.some((key) =>
				filter[key as string].some((v) => exactMatch(`${item[key]}`, `${v}`)),
			);
	}
}

const isOnSearch = (_filters: SearchKeyValueMap) =>
	Object.values(_filters).some(
		([searchTerm]) => searchTerm && `${searchTerm}`.trim().length > 0,
	);
const isOnFilter = (_filters: FilterKeyValueMap) =>
	Object.keys(_filters).length > 0;
